import { Injectable, Injector } from '@angular/core'
import { TokenService } from '../../token.service'
import { ActionApp, ActionVerb, AddLogActionGQL, TargetTypes } from '../../../../../generated/common/graphql'
import { ACTION_APP } from '../../../../sbs-common.module'

export interface LogActionDescription {
  // what: string
  changedValues?: any
  result?: any
  extra?: any
  nextlab?: {
    verb: string
    object: any
  }
}

// this is a copy of the definition in server\src\initialise\server-action-logger.service.ts
// graphql does not support union types, so we have to define the same type in two places
export enum OtherPages {
  ScienceOrganisationList = 'ScienceOrganisationList',
  QrCodeScannerPage = 'QrCodeScannerPage',
  AboutPage = 'AboutPage',
  ProfilePage = 'ProfilePage',
  WelcomePage = 'WelcomePage',
  SbsWebsite = 'SbsWebsite',
  SignInPage = 'SignInPage',
  LegalTerms = 'LegalTerms',
  ExternalPage = 'ExternalPage',
  SelfReportsList = 'SelfReportsList',
  Leaderboard = 'Leaderboard',
  ScienceProficiency = 'ScienceProficiency',
  ProficiencyPrinter = 'ProficiencyPrinter',
  Scores = 'Scores',
  ShowQrCodeForLogin = 'ShowQrCodeForLogin',
}

export type ErrorHandler = (error: any) => void

@Injectable({
  providedIn: 'root',
})
export abstract class BaseLogActionService {
  protected sessionSequenceNumber = 0
  private targetApp!: ActionApp

  private baseErrorHandler: (error: any) => void = (error) => {
    console.error('logActionService.add: error', error)
  }

  constructor(
    protected readonly addLogActionGQL: AddLogActionGQL,
    protected readonly tokenService: TokenService,
    protected readonly injector: Injector,
  ) {
    this.targetApp = this.injector.get(ACTION_APP)
  }

  private resetSessionSequenceNumber(): void {
    this.sessionSequenceNumber = 0
  }

  add(
    actionVerb: ActionVerb,
    targetType: TargetTypes,
    targetId: string,
    logActionDescription: LogActionDescription | null,
    errorHandler?: ErrorHandler | undefined,
  ) {
    const user = this.tokenService.getUser()
    if (!user) {
      console.error('logActionService.add: user not found')
    }
    const actorId = user ? user.sub : ''
    this.realAdd(actorId, actionVerb, targetType, targetId, logActionDescription ?? {}, errorHandler)
  }

  private realAdd(
    actorId: string,
    actionVerb: ActionVerb,
    targetType: TargetTypes,
    targetId: string,
    logActionDescription: LogActionDescription | null,
    errorHandler: ErrorHandler | undefined,
  ): void {
    this.addLogActionGQL
      .mutate({
        sessionSequenceNumber: this.sessionSequenceNumber++,
        actorId: actorId,
        clientDate: new Date(),
        app: this.targetApp,
        verb: actionVerb,
        targetType: targetType,
        targetId: targetId,
        description: logActionDescription,
      })
      .subscribe({
        error: errorHandler ? errorHandler : this.baseErrorHandler,
      })
  }

  logLogin(accountId: string): void {
    this.add(ActionVerb.Login, TargetTypes.Account, accountId, {}, undefined)
  }

  logLogout(accountId: string): void {
    this.realAdd(accountId, ActionVerb.Logout, TargetTypes.Account, accountId, {}, undefined)
    this.resetSessionSequenceNumber()
  }

  logError(targetType: TargetTypes, targetId: string, logActionDescription: LogActionDescription | null) {
    const user = this.tokenService.getUser()
    if (user) {
      this.addLogActionGQL
        .mutate({
          sessionSequenceNumber: this.sessionSequenceNumber++,
          actorId: user.sub,
          clientDate: new Date(),
          app: this.targetApp,
          verb: ActionVerb.Error,
          targetType: targetType,
          targetId: targetId,
          description: logActionDescription,
        })
        .subscribe({
          next: () => {
            console.log('logError: success')
          },
          error: (error) => {
            console.error('failed to send error log action', error)
          },
        })
    }
  }
}
